// Layout.js
import { Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useState } from "react";
import { CustomTable, SearchTableInput } from "../../../components";
import moment from "moment/moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import UpdateModal from "./UpdateModal";
import AdminResolveDisputeModal from "./AdminResolveDisputeModal"; // new modal (you already provided earlier)
import { useMediaQuery } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const customStyle = makeStyles({
  root: {
    padding: "20px 40px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "30px",
    overflow: "hidden",
  },
  buttonContainer: {
    display: "flex",
    gap: "20px",
    justifyContent: "end",
    alignItems: "center",
  },
  dash: {
    border: "0.5px solid #000",
    backgroundColor: "black",
    height: "1px",
    width: "10px",
  },
  datePicker: {
    width: "170px",
  },
  "@media (max-width: 390px)": {
    root: {
      padding: "5px 5px",
      gap: "10px",
    },
    buttonContainer: {
      justifyContent: "left",
      display: "flex",
      gap: "10px",
      marginTop: "10px",
    },
    dash: {
      border: "0.5px solid #000",
      backgroundColor: "black",
      height: "1px",
      width: "1px",
    },
    datePicker: {
      width: "100%",
    },
  },
});

const Layout = (props) => {
  const {
    transactionTableHeading,
    transactionTableData,
    isTableLoading,
    currentPage,
    totalLength,
    handlePageChange,
    handlePageSize,
    handleSearchQuery,
    handleTableSorting,
    updateTransId,
    setUpdateTransId,
    fetchImages,
    currentImages,
    isModalOpen,
    setIsModalOpen,
    onCloseModal,
    isModalLoading,
    menuItems,
    addedItems,
    setAddedItems,
    handleAddItem,
    handleDeleteItem,
    onChangeValue,
    handleSubmit,
    validationErrors,
    submitLoading,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    onClickDateSearch,
    images,
    currentImagesfirst,
    handleNothing,
    refreshDisputes, // provided from CartingTool
  } = props;

  const classes = customStyle();
  const isPhone = useMediaQuery("(max-width: 390px)");

  // Local state for dispute modal
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [selectedTransactionForResolve, setSelectedTransactionForResolve] = useState(null);

  const actions = [
    {
      label: "Update",
      onClick: (row) => {
        fetchImages(row);
        setIsModalOpen(true);
      },
      bgColor: "#2196F3",
    },
  ];

  // Build safe headings and append Dispute column dynamically.
  const safeHeadings = Array.isArray(transactionTableHeading)
    ? transactionTableHeading
    : Array.from(transactionTableHeading || []);
  // Append a column that will look at the _dispute field on the row object
  const disputeColumn = {
    key: "_dispute", // CustomTable will call row['_dispute'] and pass to format
    label: "Dispute",
    format: (disputeObj) => {
      // If there is no dispute object, show nothing
      if (!disputeObj) return "";
      // disputeObj.status -> "active" | "resolved"
      if (disputeObj.status === "active") {
        // return a small red icon (we render as simple string or emoji here, but CustomTable expects a React node)
        // Note: CustomTable will render the returned value directly inside a TableCell.
        return <ErrorOutlineIcon style={{ color: "red", cursor: "pointer" }} />;
      }
      // resolved
      return <CheckCircleOutlineIcon style={{ color: "green", cursor: "pointer" }} />;
    },
  };

  const finalHeadings = [...safeHeadings, disputeColumn];

  // When user clicks on the dispute icon, we need a handler.
  // But CustomTable passes only action buttons via `actions` prop.
  // We need to allow clicking the icon rendered via column.format.
  // To do so, we'll wrap the icon in a clickable element by altering the data rows:
  // However we already enriched rows with _dispute object; here we'll detect click by rendering a button around the icon.
  // Because CustomTable calls column.format(value) and places directly, we must ensure the returned React node has onClick handler that uses selectedTransaction row.
  // But column.format only receives the value. We need the row to be able to reference transaction id; we will use the fact that CustomTable uses row[column.key] — so the value is the dispute object, which does not include transaction id.
  // To pass the transaction row context we rely on closure: instead, we'll override transactionTableData before passing to CustomTable to include a helper function or to include the dispute object with a `__txId` field.
  // However we don't want to change CustomTable. The simpler approach: We'll create a wrapper format that returns a small component which looks up the parent row by reading a `data-txid` attribute (set below when generating the data).
  // To keep things clean, we will actually expect that the rows passed in (transactionTableData) have `id` and `_dispute`. column.format will return a clickable element that when clicked emits a custom event with tx id; we'll handle it here via onClick passed as part of the node.
  // But column.format doesn't receive row, so we instead will override to show icon wrapped in a small clickable element that uses disputeObj.docId and disputeObj to call window-level function.
  // To keep it simple and reliable, we'll attach a click handler here by returning a small <span onClick=...> using disputeObj.docId and disputeObj (assuming disputeObj.docData.transactionID is present). We'll call local openResolveModal with the transaction id from disputeObj.docData.transactionID (or fallback).
  // Note: disputeObj.docData should include transactionID property.

  // Helper to open modal (we pass the transaction row id and row object)
  const openResolveModal = (transactionRow) => {
    setSelectedTransactionForResolve(transactionRow);
    setResolveModalOpen(true);
  };

  // We'll create a modified finalHeadings where format uses a function that expects disputeObj plus we return clickable node
  const finalHeadingsWithClickableDispute = finalHeadings.map((col) => {
    if (col.key !== "_dispute") return col;
    return {
      ...col,
      format: (disputeObj, /* note CustomTable only passes one arg (value) */) => {
        if (!disputeObj) return "";
        // If disputeObj.docData exists and contains transactionID, use it; else fallback to docData.transactionID or docData.transactionid
        const status = disputeObj.status;
        // Render clickable icon; we include data-docid so the click handler can find it.
        if (status === "active") {
          return (
            <span
              onClick={(e) => {
                // Prevent propagation
                e.stopPropagation();
                // build minimal transaction object to pass to modal
                const txId = disputeObj.docData?.transactionID || disputeObj.docData?.transactionid || disputeObj.docId;
                openResolveModal({ id: txId });
              }}
              style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
              title="Active dispute - click to view/resolve"
            >
              <ErrorOutlineIcon style={{ color: "red" }} />
            </span>
          );
        }
        // resolved
        return (
          <span
            onClick={(e) => {
              e.stopPropagation();
              const txId = disputeObj.docData?.transactionID || disputeObj.docData?.transactionid || disputeObj.docId;
              openResolveModal({ id: txId });
            }}
            style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
            title="Resolved dispute - click to view"
          >
            <CheckCircleOutlineIcon style={{ color: "green" }} />
          </span>
        );
      },
    };
  });

  return (
    <>
      <div className={classes.root}>
        <div
          className="flex justify-content-space-between"
          style={{
            display: "flex",
            flexDirection: isPhone ? "column" : "row",
            alignItems: isPhone ? "flex-start" : "center",
          }}
        >
          <SearchTableInput tableHeading={transactionTableHeading} handleSearchQuery={handleSearchQuery} width={"200px"} />
          <div className={classes.buttonContainer}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                className={classes.datePicker}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: isPhone ? "0.50rem" : "auto",
                    maxWidth: isPhone ? "70%" : "auto",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: isPhone ? "0.75rem" : "auto",
                  },
                }}
              />
              <span className={classes.dash} />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                className={classes.datePicker}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: isPhone ? "0.50rem" : "auto",
                    maxWidth: isPhone ? "70%" : "auto",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: isPhone ? "0.75rem" : "auto",
                  },
                  marginLeft: isPhone ? "-60px" : "auto",
                }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              style={{
                color: "white",
                backgroundColor: "#FF5722",
                padding: isPhone ? "6px 14px" : "12px 24px",
                borderRadius: "10px",
                marginLeft: isPhone ? "-50px" : "auto",
                fontSize: isPhone ? "0.900rem" : "1rem",
              }}
              onClick={onClickDateSearch}
            >
              Search
            </Button>
          </div>
        </div>

        <CustomTable
          columns={finalHeadingsWithClickableDispute}
          data={transactionTableData}
          actions={actions}
          actionsHeader={"Update Transaction"}
          isLoading={isTableLoading}
          currentPage={currentPage}
          totalLength={totalLength}
          handlePageChange={handlePageChange}
          handlePageSize={handlePageSize}
          handleTableSorting={handleTableSorting}
        />
      </div>

      <UpdateModal
        isModalOpen={isModalOpen}
        isLoading={isModalLoading}
        onClose={onCloseModal}
        currentImages={currentImages}
        menuItems={menuItems}
        addedItems={addedItems}
        setAddedItems={setAddedItems}
        handleAddItem={handleAddItem}
        handleDeleteItem={handleDeleteItem}
        onChangeValue={onChangeValue}
        handleSubmit={handleSubmit}
        validationErrors={validationErrors}
        submitLoading={submitLoading}
        images={images}
        currentImagesfirst={currentImagesfirst}
        handleNothing={handleNothing}
      />

      <AdminResolveDisputeModal
        open={resolveModalOpen}
        onClose={() => setResolveModalOpen(false)}
        transaction={selectedTransactionForResolve}
        onResolved={() => {
          // refresh parent data (CartingTool)
          if (typeof refreshDisputes === "function") refreshDisputes();
          setResolveModalOpen(false);
        }}
      />
    </>
  );
};

export default Layout;
